import {} from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useState } from 'react';
import checkMarkIcon from 'src/assets/img/checkmarkicon.svg';

import { buildJourneyCards, IJourneyCard, IQuotePreparingLaneContext } from './laneInsights';
import { PhaseStepState, useSimulatedPhases } from './useSimulatedPhases';

import './quotePreparingLoader.css';

interface IQuotePreparingLoaderProps {
    laneContext?: IQuotePreparingLaneContext | null;
    // Parent flips this to true when the active quotes have arrived; the
    // loader plays its completion beat (steps done, bar 100%) then calls onSettled.
    isSettling: boolean;
    onSettled: () => void;
}

// A sales-rep-priced quote has no machine progress to narrate and no honest time estimate — it
// waits on a person, sometimes for hours. 4h keeps useSimulatedPhases from auto-advancing step 3
// ("Quote sent to you" must never light up before the quote actually lands — only `isSettling`
// completes it) and from ever reaching the overtime subtitle below.
const MANAGED_TYPICAL_MS = 4 * 60 * 60 * 1000;

// Customer-safe journey wording. HARD RULE: never mention rate counts, carrier counts, or pricing
// rules — portal customers see a curated quote (sometimes a single option), so the story must not
// hint at what happens behind the scenes.
//
// Two variants (client decision, 2026-10-01): an Instant LTL quote rates itself off the carrier
// network in seconds, while Volume LTL / FTL / Specialized is picked up and priced by a sales rep.
// Telling that second group "checking rates across our carrier network" promised an automated
// result that was never coming, under a progress bar that could sit there for hours. Which variant
// applies is decided server-side — see the BE's `isInstantLtl` (utils/instantLtlQuote.ts), derived
// from the admin Volume-LTL thresholds in Settings -> Quote Settings.
const JOURNEY_COPY = {
    instant: {
        title: 'Preparing your quote',
        subtitle: 'Usually takes a few moments',
        step2: 'Checking rates across our carrier network',
        step2Hint: 'Comparing service levels and transit times',
        step3: 'Finalizing your quote options',
        typicalMs: 30000,
    },
    managed: {
        title: 'Your quote is with our team',
        subtitle: 'A sales rep will send your quote shortly',
        step2: 'Sales rep reviewing your shipment',
        // No hint: anything here would be a guess about what the rep is doing at this moment.
        step2Hint: undefined as string | undefined,
        step3: 'Quote sent to you',
        typicalMs: MANAGED_TYPICAL_MS,
    },
};

const OVERTIME_SUBTITLE = 'Taking a little longer than usual — still working on it';
// Fixed bar-sweep duration, same for both journey variants — independent of copy.typicalMs, which
// still paces the checklist steps/overtime message via useSimulatedPhases. The sweep caps at 90%
// (see quotePreparingLoader.css) and only snaps to 100% on settle, so a sales-rep quote that sits
// here for hours never reads as finished.
const BAR_DURATION_MS = 180000;
const CARD_ROTATION_MS = 5000;
// Matches .qpl-tip-out / .qpl-tip-in durations in quotePreparingLoader.css.
// Sequential, not concurrent: the outgoing card must fully disappear before
// the next one appears, or both are legible at once mid-transition.
const CARD_EXIT_MS = 450;
const CARD_ENTER_MS = 550;

// Renders **bold** markers from card copy as emphasized spans.
const renderBoldMarkers = (line: string) =>
    line.split(/\*\*(.*?)\*\*/g).map((part, idx) =>
        idx % 2 === 1 ? (
            <b key={idx} className="font-semibold text-textSecondary">
                {part}
            </b>
        ) : (
            <React.Fragment key={idx}>{part}</React.Fragment>
        )
    );

const TimelineDot = ({ state }: { state: PhaseStepState }) => {
    if (state === 'done') {
        return (
            <>
                <img src={checkMarkIcon} alt="Done" className="w-[28px] h-[28px] flex-none pl-[1px] z-[2]" />
            </>
        );
    }

    if (state === 'active') {
        return (
            <div className="w-[28px] h-[28px] flex-none flex items-center justify-center">
                <span className="qpl-dot-active w-[10px] h-[10px] flex-none rounded-full bg-primary z-[1]" />
            </div>
        );
    }

    return (
        <>
            <div className="flex-none flex items-center justify-center self-start w-[28px] h-[28px]">
                <span className="w-4 h-4 rounded-full bg-utilityGray200 flex-none mt-0.5 z-[1]" />
            </div>
        </>
    );
};

// One face of the Lane Intelligence card (kicker · headline · body)
const JourneyCardFace = ({ card, index }: { card: IJourneyCard; index: number }) => (
    <>
        <p className="m-0 text-[10px] font-bold uppercase tracking-widest text-primary">
            <span className="text-primary opacity-40 font-semibold mr-2">{String(index + 1).padStart(2, '0')}</span>
            {card.kicker}
        </p>
        <p className="m-0 mt-2 text-base font-semibold text-grayLight900 leading-snug">{card.headline}</p>
        <p className="m-0 mt-1.5 text-xs text-gray500 leading-relaxed">{renderBoldMarkers(card.body)}</p>
    </>
);

const QuotePreparingLoader: React.FC<IQuotePreparingLoaderProps> = ({ laneContext, isSettling, onSettled }) => {
    const ctx: IQuotePreparingLaneContext = laneContext || {};
    // Only switch to the sales-rep story once the payload has positively said so — while
    // isInstantLtl is still undefined (quote fetch still in flight on a hard refresh) the instant
    // wording is the safer default: it promises nothing about a person being involved.
    const copy = ctx.isInstantLtl === false ? JOURNEY_COPY.managed : JOURNEY_COPY.instant;

    const phases = useSimulatedPhases(copy.typicalMs, isSettling, onSettled);

    const cards = useMemo(() => buildJourneyCards(ctx), [laneContext]); // eslint-disable-line react-hooks/exhaustive-deps
    // Sequential exit → enter, never both mounted at once (concurrent fade
    // in/out made two cards' text simultaneously legible mid-transition).
    const [display, setDisplay] = useState<{ index: number; phase: 'idle' | 'exiting' | 'entering' }>({ index: 0, phase: 'idle' });

    useEffect(() => {
        if (cards.length < 2) return undefined;

        // Reduced motion: CSS kills the animations, but the rotation itself must
        // also stop (show the first card statically).
        if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return undefined;
        }
        const interval = setInterval(() => {
            setDisplay((old) => ({ ...old, phase: 'exiting' }));
        }, CARD_ROTATION_MS);

        return () => clearInterval(interval);
    }, [cards.length]);

    useEffect(() => {
        if (display.phase !== 'exiting') return undefined;
        const timeout = setTimeout(() => {
            setDisplay((old) => ({ index: (old.index + 1) % cards.length, phase: 'entering' }));
        }, CARD_EXIT_MS);

        return () => clearTimeout(timeout);
    }, [display.phase, cards.length]);

    useEffect(() => {
        if (display.phase !== 'entering') return undefined;
        const timeout = setTimeout(() => setDisplay((old) => ({ ...old, phase: 'idle' })), CARD_ENTER_MS);

        return () => clearTimeout(timeout);
    }, [display.phase]);

    // buildJourneyCards([]) only happens before quotingDetails has loaded at all
    // (e.g. hard refresh, no router nav state yet) — this loader is only ever
    // mounted while a quote is actively loading, so an empty card list here
    // always means "not ready yet", not "genuinely no lane data". Render a
    // skeleton matching the real stack 1:1 rather than hiding the panel.
    const hasCards = cards.length > 0;

    const steps: { label: string; hint?: string; state: PhaseStepState }[] = [
        { label: 'Shipment details received', state: phases.steps[0] },
        { label: copy.step2, hint: copy.step2Hint, state: phases.steps[1] },
        { label: copy.step3, state: phases.steps[2] },
    ];

    return (
        // Left column stays put at a fixed spot regardless of hasCards — the lane
        // panel fills in on the right as laneContext arrives (progressive reveal,
        // not a component swap), so the timeline never jumps position.
        <div className="w-full flex mdm:items-center max-mdm:flex-col max-mdm:gap-y-6 mdm:p-6 p-4 mdm:min-h-[240px]" data-testid="quote-preparing-loader">
            <div className="mdm:w-1/2 w-full mdm:pr-12" role="status" aria-live="polite">
                <h6 className="text-sm font-semibold  text-textSecondary">{copy.title}</h6>
                <p className="text-xs leading-[1.5] font-normal text-grayLight600">{phases.isOvertime ? OVERTIME_SUBTITLE : copy.subtitle}</p>

                <ul className="!mt-5">
                    {steps.map((step) => (
                        <li key={step.label} className="steps-item steps-item-done">
                            <div className={`steps-item-icon-main after:!-bottom-3.5 `}>
                                <TimelineDot state={step.state} />
                            </div>
                            <div className="steps-item-name-wrap !self-start mt-1">
                                <p className={``}>{step.label}</p>
                                {step.state === 'active' && step.hint && <p className="text-[11px] leading-[14px] font-normal text-gray500 mt-0.5">{step.hint}</p>}
                            </div>
                        </li>
                    ))}
                </ul>

                <div className="flex items-center gap-3 mt-5">
                    <div className="flex-1 h-[3px] bg-utilityGray200 rounded-full">
                        <div
                            className={`qpl-bar-fill relative h-full rounded-full bg-primary ${isSettling ? 'qpl-bar-fill-complete' : 'qpl-bar-fill-animating'}`}
                            style={isSettling ? undefined : ({ '--qpl-bar-duration': `${BAR_DURATION_MS}ms` } as React.CSSProperties)}
                        />
                    </div>
                    <span className="text-[11px] text-gray500 whitespace-nowrap">step {phases.activeStepNumber} of 3</span>
                </div>
            </div>

            <div className="mdm:w-1/2 w-full border-gray100 mdm:border-l mdm:pl-12 max-mdm:border-t max-mdm:pt-5">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray500 mb-3.5">Lane intelligence</p>

                <div className="relative h-[148px]">
                    {hasCards ? (
                        <>
                            {cards.length > 1 && (
                                <>
                                    <span aria-hidden="true" className="qpl-stack-ghost qpl-stack-ghost-far" />
                                    <span aria-hidden="true" className="qpl-stack-ghost" />
                                </>
                            )}
                            <div className="qpl-stack-card absolute inset-0 bg-white rounded-xl px-5 py-[18px] z-[1]">
                                <div className="relative h-full">
                                    {/* Only ever one card mounted — exit fully completes before the next enters, so
                                        two cards' text is never simultaneously legible (see CARD_EXIT_MS/CARD_ENTER_MS). */}
                                    <div
                                        key={`card-${display.index}-${display.phase}`}
                                        className={`absolute inset-0 ${display.phase === 'exiting' ? 'qpl-tip-out' : display.phase === 'entering' ? 'qpl-tip-in' : ''}`}
                                    >
                                        {/* Index clamped in case the card list ever shrinks (future Phase B lane-history entries) */}
                                        <JourneyCardFace card={cards[display.index % cards.length]} index={display.index % cards.length} />
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        // Lane context hasn't resolved yet (hard refresh — no router nav
                        // state until the quote details fetch lands). Skeleton mirrors the
                        // real card stack 1:1 so there's no jump/reflow once it does.
                        <div aria-hidden="true">
                            <span className="qpl-stack-ghost qpl-stack-ghost-far" />
                            <span className="qpl-stack-ghost" />
                            <div className="qpl-stack-card absolute inset-0 bg-white rounded-xl px-5 py-[18px] z-[1]">
                                <p className="custom-loading m-0 text-[10px] font-bold uppercase tracking-widest w-24">Pickup — City, ST</p>
                                <p className="custom-loading m-0 mt-2.5 text-base font-semibold w-40 h-4">Headline placeholder</p>
                                <p className="custom-loading m-0 mt-2.5 text-xs w-full h-3">Body copy placeholder line</p>
                                <p className="custom-loading m-0 mt-1.5 text-xs w-4/5 h-3">Body copy placeholder line two</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuotePreparingLoader;
